import { useMutation, useQueryClient } from '@tanstack/react-query';

import { updateHarness } from '@/lib/api/generated/api';

import { toHarnessError } from '../errors';
import { harnessKeys } from '../queryKeys';
import type { Harness, HarnessWriteInput } from '../types';

/**
 * Edit an existing Harness.
 *
 * `body.token` being omitted leaves the stored token unchanged (spec FR-007)
 * — that contract is enforced server-side (via `model_fields_set`); this hook
 * only forwards whatever the form sent, and never sends `harness_type`
 * (absent from `UpdateHarnessRequest` by design — changing type after
 * creation is not supported). `wire_api` omitted likewise means unchanged;
 * the form sends it only for OpenAI harnesses.
 */
export function useUpdateHarness() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: string;
      body: HarnessWriteInput;
    }): Promise<Harness> => {
      const response = await updateHarness(id, {
        name: body.name,
        base_url: body.base_url,
        token: body.token,
        ...(body.wire_api === undefined ? {} : { wire_api: body.wire_api }),
      });
      if (response.status === 200) return response.data;
      throw toHarnessError(response.status, response.data);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: harnessKeys.all() });
    },
  });
}
